// Pannello notifiche slide-over da destra
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Bell, X, Check, CheckCheck, Info, AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react'
import { cn, formatRelativeDate } from '../../lib/utils'
import { useNotificationsStore, type AppNotification } from '../../stores/notifications.store'
import { useNotifications } from '../../hooks/useNotifications'
import { Button } from '../ui/Button'

const TYPE_ICONS: Record<AppNotification['type'], React.ReactNode> = {
  info: <Info className="h-4 w-4 text-[var(--fc-info)]" />,
  success: <CheckCircle className="h-4 w-4 text-[var(--fc-success)]" />,
  warning: <AlertTriangle className="h-4 w-4 text-[var(--fc-warning)]" />,
  error: <AlertCircle className="h-4 w-4 text-[var(--fc-danger)]" />,
}

interface NotificationsPanelProps {
  open: boolean
  onClose: () => void
}

export function NotificationsPanel({ open, onClose }: NotificationsPanelProps) {
  const notifications = useNotificationsStore((s) => s.notifications)
  const { markAsRead, markAllAsRead } = useNotifications()

  // Chiudi con Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/30"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <div
        className={cn(
          'fixed right-0 top-0 z-50 flex h-full w-80 flex-col border-l border-[var(--fc-border)] bg-[var(--fc-surface)] shadow-[var(--fc-shadow-lg)] transition-transform duration-300',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Pannello notifiche"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--fc-border)] px-4 py-3">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-[var(--fc-foreground-muted)]" />
            <h2 className="text-base font-semibold text-[var(--fc-foreground)]">Notifiche</h2>
          </div>
          <div className="flex items-center gap-2">
            {notifications.some((n) => !n.read) && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => markAllAsRead()}
                title="Segna tutte come lette"
                aria-label="Segna tutte come lette"
              >
                <CheckCheck className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Chiudi">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Lista notifiche */}
        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Bell className="mb-3 h-10 w-10 text-[var(--fc-foreground-subtle)]" aria-hidden="true" />
              <p className="text-sm text-[var(--fc-foreground-muted)]">Nessuna notifica</p>
            </div>
          ) : (
            <ul role="list">
              {notifications.map((notification) => (
                <li
                  key={notification.id}
                  className={cn(
                    'flex items-start gap-3 border-b border-[var(--fc-border)] px-4 py-3 last:border-0',
                    !notification.read && 'bg-[color-mix(in_srgb,var(--fc-primary)_4%,transparent)]'
                  )}
                >
                  <span className="mt-0.5 shrink-0">{TYPE_ICONS[notification.type]}</span>
                  <div className="min-w-0 flex-1">
                    <p className={cn(
                      'text-sm text-[var(--fc-foreground)]',
                      !notification.read && 'font-semibold'
                    )}>
                      {notification.link ? (
                        <Link
                          to={notification.link}
                          className="hover:text-[var(--fc-primary)] transition-colors"
                          onClick={onClose}
                        >
                          {notification.title}
                        </Link>
                      ) : (
                        notification.title
                      )}
                    </p>
                    {notification.message && (
                      <p className="mt-0.5 text-xs text-[var(--fc-foreground-muted)]">
                        {notification.message}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-[var(--fc-foreground-subtle)]">
                      {formatRelativeDate(notification.createdAt)}
                    </p>
                  </div>
                  {!notification.read && (
                    <button
                      onClick={() => markAsRead(notification.id)}
                      className="shrink-0 text-[var(--fc-foreground-muted)] hover:text-[var(--fc-primary)] transition-colors"
                      aria-label="Segna come letta"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  )
}
